class ImageCache {
  private imageDataCache = new Map<string, string>();
  private loadingPromises = new Map<string, Promise<boolean>>();
  private preloadContainer: HTMLDivElement | null = null;
  constructor() {
    this.createPreloadContainer();
  }
  async preloadImage(list: string[]): Promise<void> {
    const uniqueList = [...new Set(list)]; // 去重

    const preloadPromises = uniqueList.map(async item => {
      // 如果已经在缓存中或正在加载，跳过
      if (this.imageDataCache.has(item) || this.loadingPromises.has(item)) {
        return this.loadingPromises.get(item) || Promise.resolve(true);
      }

      const loadPromise = this.loadSingleImage(item);
      this.loadingPromises.set(item, loadPromise);

      try {
        await loadPromise;
      } finally {
        this.loadingPromises.delete(item);
      }

      return loadPromise;
    });

    try {
      await Promise.allSettled(preloadPromises); // 使用 allSettled 避免单个失败影响整体
    } catch (err) {
      console.error('preloadImage-error:', err);
    }
  }

  private async loadSingleImage(url: string): Promise<boolean> {
    try {
      return new Promise<boolean>(resolve => {
        const img = new Image();
        img.onload = async () => {
          try {
            await img.decode(); // 确保图片完全解码
            this.imageDataCache.set(url, url); // 缓存原始 URL
            this.preloadContainer?.removeChild(img);
            resolve(true);
          } catch (error) {
            resolve(false);
          }
        };
        img.onerror = () => {
          resolve(false);
        };
        // 设置加载超时
        setTimeout(() => {
          if (!this.imageDataCache.has(url)) {
            resolve(false);
          }
        }, 10000); // 10秒超时
        img.src = url;
        this.preloadContainer?.appendChild(img);
      });
    } catch (error) {
      return false;
    }
  }

  // 批量预加载，支持优先级
  async preloadImagesByPriority(
    highPriorityImages: string[],
    lowPriorityImages: string[] = [],
  ): Promise<void> {
    // 先加载高优先级图片
    await this.preloadImage(highPriorityImages);

    // 然后加载低优先级图片（不阻塞）
    if (lowPriorityImages.length > 0) {
      this.preloadImage(lowPriorityImages).catch(err =>
        console.warn('preloadImagesByPriority-error:', err),
      );
    }
  }

  // 检查图片是否已缓存
  isImageCached(url: string): boolean {
    return this.imageDataCache.has(url);
  }

  // 获取预处理的图片 URL
  getOptimizedImageUrl(originalUrl: string): string {
    return this.imageDataCache.get(originalUrl) || originalUrl;
  }

  // 清理缓存
  clearCache(): void {
    // 清理 blob URLs 以避免内存泄漏
    this.imageDataCache.forEach((blobUrl, originalUrl) => {
      console.log(originalUrl);
      if (blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
      }
    });
    this.imageDataCache.clear();
    this.loadingPromises.clear();
  }

  // 获取缓存状态
  getCacheInfo(): {cached: number; loading: number} {
    return {
      cached: this.imageDataCache.size,
      loading: this.loadingPromises.size,
    };
  }
  private createPreloadContainer() {
    if (typeof window !== 'undefined' && !this.preloadContainer) {
      this.preloadContainer = document.createElement('div');
      this.preloadContainer.id = 'preload-container';
      this.preloadContainer.style.cssText = `
        position: fixed;
        top: -9999px;
        left: -9999px;
        width: 1px;
        height: 1px;
        overflow: hidden;
        opacity: 0;
        pointer-events: none;
        z-index: 10;
        transform: translateZ(0);
      `;
      document.body.appendChild(this.preloadContainer);
    }
  }
}

export const imageCache = new ImageCache();

// 页面卸载时清理缓存
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    imageCache.clearCache();
  });
}
